import { useEffect, useMemo, useState } from "react"
import { Loader2, Upload, Trash2, Plus, CheckCircle2, ListChecks, FileText } from "lucide-react"
import AdminSidebar from "../components/AdminSidebar"
import ConfirmModal from "../components/ConfirmModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` }
}

export default function AdminResultsIngestion() {
  const [siteEvents, setSiteEvents] = useState([])
  const [loadingSiteEvents, setLoadingSiteEvents] = useState(true)
  const [siteEventId, setSiteEventId] = useState("")

  const [parsing, setParsing] = useState(false)
  const [error, setError] = useState("")
  const [toast, setToast] = useState("")

  // Flat list of parsed-but-unpublished rows across every race in the batch.
  const [parsedRows, setParsedRows] = useState([])
  const [selectedEventName, setSelectedEventName] = useState(null)
  const [publishing, setPublishing] = useState(false)

  const [published, setPublished] = useState([])
  const [loadingPublished, setLoadingPublished] = useState(true)
  const [deletingEvent, setDeletingEvent] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const { t } = useLanguage()

  const siteEventsById = useMemo(() => Object.fromEntries(siteEvents.map((e) => [e.id, e])), [siteEvents])

  // Each unique "Event N ..." header parsed out of the PDF — one HY-TEK race,
  // not to be confused with the real site Event these get published under.
  const races = useMemo(() => {
    const byName = new Map()
    for (const row of parsedRows) {
      if (!byName.has(row.event_name)) {
        byName.set(row.event_name, { event_name: row.event_name, event_number: row.event_number, gender: row.gender, count: 0 })
      }
      byName.get(row.event_name).count += 1
    }
    return [...byName.values()].sort((a, b) => a.event_number - b.event_number)
  }, [parsedRows])

  const selectedRows = useMemo(
    () => parsedRows.filter((r) => r.event_name === selectedEventName),
    [parsedRows, selectedEventName],
  )

  const fetchSiteEvents = async () => {
    setLoadingSiteEvents(true)
    try {
      const res = await fetch(`${API}/api/admin/meet-results/events`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch events")
      setSiteEvents(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingSiteEvents(false)
    }
  }

  const fetchPublished = async () => {
    setLoadingPublished(true)
    try {
      const res = await fetch(`${API}/api/results`)
      if (!res.ok) throw new Error("Failed to fetch published results")
      const rows = await res.json()
      const byName = new Map()
      for (const row of rows) {
        if (!byName.has(row.event_name)) {
          byName.set(row.event_name, { event_name: row.event_name, event_id: row.event_id, gender: row.gender, count: 0 })
        }
        byName.get(row.event_name).count += 1
      }
      setPublished([...byName.values()].sort((a, b) => a.event_name.localeCompare(b.event_name)))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingPublished(false)
    }
  }

  useEffect(() => { fetchSiteEvents(); fetchPublished() }, [])

  const handleFile = async (e) => {
    const file = e.target.files[0]
    e.target.value = ""
    if (!file) return
    setError("")
    setParsing(true)
    try {
      const fd = new FormData()
      fd.append("file", file)
      const res = await fetch(`${API}/api/admin/meet-results/parse-pdf`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.detail || "Failed to parse PDF")
      setParsedRows(data.results.map((r, i) => ({ ...r, _localId: `${Date.now()}-${i}` })))
      setSelectedEventName(data.results[0]?.event_name ?? null)
      setToast(t("admin_ingest_toast_parsed").replace("{count}", data.results.length))
      setTimeout(() => setToast(""), 4000)
    } catch (e) {
      setError(e.message)
    } finally {
      setParsing(false)
    }
  }

  const updateRow = (localId, field, value) => {
    setParsedRows((rows) => rows.map((r) => (r._localId === localId ? { ...r, [field]: value } : r)))
  }

  const removeRow = (localId) => {
    setParsedRows((rows) => rows.filter((r) => r._localId !== localId))
  }

  const addRow = () => {
    if (!selectedEventName) return
    const context = selectedRows[0]
    setParsedRows((rows) => [
      ...rows,
      {
        _localId: `${Date.now()}-manual`,
        event_name: selectedEventName,
        event_number: context?.event_number ?? 0,
        gender: context?.gender ?? "Boys",
        rank: "",
        athlete: "",
        age: "",
        team: "",
        time: "",
      },
    ])
  }

  const publishEvent = async () => {
    if (!selectedRows.length || !siteEventId) return
    setPublishing(true)
    setError("")
    try {
      const { event_number, gender } = selectedRows[0]
      const res = await fetch(`${API}/api/admin/meet-results/publish`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: siteEventId,
          event_number,
          event_name: selectedEventName,
          gender,
          results: selectedRows.map((r) => ({
            rank: r.rank, athlete: r.athlete, age: Number(r.age) || 0, team: r.team, time: r.time,
          })),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || "Failed to publish event")
      }
      setParsedRows((rows) => rows.filter((r) => r.event_name !== selectedEventName))
      setSelectedEventName((prev) => {
        const remaining = races.filter((r) => r.event_name !== prev)
        return remaining[0]?.event_name ?? null
      })
      const fallbackEvent = t("admin_ingest_fallback_event")
      setToast(t("admin_ingest_toast_published").replace("{race}", selectedEventName).replace("{event}", siteEventsById[siteEventId]?.title ?? fallbackEvent))
      setTimeout(() => setToast(""), 4000)
      fetchPublished()
    } catch (e) {
      setError(e.message)
    } finally {
      setPublishing(false)
    }
  }

  const confirmDeletePublishedEvent = async () => {
    const eventName = deleteTarget
    setDeletingEvent(eventName)
    try {
      const res = await fetch(`${API}/api/admin/meet-results/event`, {
        method: "DELETE",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ event_name: eventName }),
      })
      if (!res.ok) throw new Error("Failed to delete event")
      setDeleteTarget(null)
      await fetchPublished()
    } catch (e) {
      setError(e.message)
      setDeleteTarget(null)
    } finally {
      setDeletingEvent(null)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <AdminSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8">
          <h1 className="flex items-center gap-3 font-display text-3xl font-bold tracking-tight text-paper-0">
            <ListChecks className="h-7 w-7 text-gold-400" /> {t("admin_ingest_title")}
          </h1>
          <p className="mt-1 text-sm text-mist-300">
            {t("admin_ingest_subtitle")}
          </p>
        </header>

        {error && <div role="alert" className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
        {toast && (
          <div role="status" className="mb-6 flex items-center gap-2 rounded border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> {toast}
          </div>
        )}

        {/* Step 1: choose the real site event */}
        <div className="mb-8 rounded-xl border border-white/10 bg-ink-800/20 p-6">
          <h2 className="mb-1 font-display text-lg font-bold text-paper-0">{t("admin_ingest_step1_title")}</h2>
          <p className="mb-4 text-sm text-mist-400">{t("admin_ingest_step1_desc")}</p>
          {loadingSiteEvents ? (
            <Loader2 className="h-5 w-5 animate-spin text-gold-400" />
          ) : (
            <select
              value={siteEventId}
              onChange={(e) => setSiteEventId(e.target.value)}
              className="h-8 w-full max-w-xs rounded-lg border border-white/15 bg-ink-700/60 px-2.5 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20"
            >
              <option value="">{t("admin_ingest_select_event")}</option>
              {siteEvents.map((e) => (
                <option key={e.id} value={e.id}>{e.title} — {e.date}</option>
              ))}
            </select>
          )}
        </div>

        {/* Step 2: upload results PDF */}
        <div className={`mb-8 rounded-xl border border-white/10 bg-ink-800/20 p-6 ${!siteEventId ? "opacity-50" : ""}`}>
          <h2 className="mb-1 font-display text-lg font-bold text-paper-0">{t("admin_ingest_step2_title")}</h2>
          <p className="mb-4 text-sm text-mist-400">
            {siteEventId
              ? t("admin_ingest_step2_desc1")
              : t("admin_ingest_step2_desc2")}
          </p>
          <label className={`inline-flex h-8 items-center gap-1.5 rounded-lg bg-gold-500 px-3 text-xs font-semibold text-ink-900 transition-colors hover:bg-gold-400 ${!siteEventId || parsing ? "pointer-events-none opacity-70" : "cursor-pointer"}`}>
            {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {parsing ? t("admin_ingest_parsing") : t("admin_ingest_choose_pdf")}
            <input type="file" accept=".pdf" onChange={handleFile} disabled={!siteEventId || parsing} className="hidden" />
          </label>
        </div>

        {/* Step 3: review/edit parsed races, then publish */}
        {races.length > 0 && (
          <div className="mb-8 rounded-xl border border-white/10 bg-ink-800/20 p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-paper-0">{t("admin_ingest_step3_title")}</h2>
              <button
                onClick={publishEvent}
                disabled={publishing || !selectedRows.length || !siteEventId}
                className="flex h-8 items-center gap-1.5 rounded-lg bg-gold-500 px-3 text-xs font-semibold text-ink-900 transition-colors hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {t("admin_ingest_publish_btn")}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
              {/* Left: race list */}
              <div className="flex flex-col gap-1.5 lg:border-r lg:border-white/10 lg:pr-4">
                {races.map((ev) => (
                  <button
                    key={ev.event_name}
                    onClick={() => setSelectedEventName(ev.event_name)}
                    className={`rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedEventName === ev.event_name
                        ? "bg-gold-500/10 text-gold-400 font-semibold"
                        : "text-mist-300 hover:bg-white/5 hover:text-paper-0"
                    }`}
                  >
                    <span className="block truncate">{ev.event_name}</span>
                    <span className="text-xs text-mist-500">{ev.count} {t("admin_ingest_results_count")}</span>
                  </button>
                ))}
              </div>

              {/* Right: editable grid for selected race */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-white/10 text-xs font-semibold uppercase tracking-wider text-mist-400">
                    <tr>
                      <th className="px-3 py-2">{t("admin_ingest_col_rank")}</th>
                      <th className="px-3 py-2">{t("admin_ingest_col_athlete")}</th>
                      <th className="px-3 py-2">{t("admin_ingest_col_age")}</th>
                      <th className="px-3 py-2">{t("admin_ingest_col_team")}</th>
                      <th className="px-3 py-2">{t("admin_ingest_col_time")}</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {selectedRows.map((row) => (
                      <tr key={row._localId}>
                        <td className="px-3 py-2">
                          <input value={row.rank} onChange={(e) => updateRow(row._localId, "rank", e.target.value)} className="h-8 w-16 rounded border border-white/15 bg-ink-700/60 px-2 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20" />
                        </td>
                        <td className="px-3 py-2">
                          <input value={row.athlete} onChange={(e) => updateRow(row._localId, "athlete", e.target.value)} className="h-8 w-full min-w-[140px] rounded border border-white/15 bg-ink-700/60 px-2 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20" />
                        </td>
                        <td className="px-3 py-2">
                          <input value={row.age} onChange={(e) => updateRow(row._localId, "age", e.target.value)} className="h-8 w-14 rounded border border-white/15 bg-ink-700/60 px-2 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20" />
                        </td>
                        <td className="px-3 py-2">
                          <input value={row.team} onChange={(e) => updateRow(row._localId, "team", e.target.value)} className="h-8 w-full min-w-[140px] rounded border border-white/15 bg-ink-700/60 px-2 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20" />
                        </td>
                        <td className="px-3 py-2">
                          <input value={row.time} onChange={(e) => updateRow(row._localId, "time", e.target.value)} className="h-8 w-24 rounded border border-white/15 bg-ink-700/60 px-2 text-xs text-paper-0 focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20" />
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeRow(row._localId)} aria-label="Remove row" className="flex h-7 w-7 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-400 transition-colors hover:bg-red-500 hover:text-white">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  onClick={addRow}
                  disabled={!selectedEventName}
                  className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-gold-400 transition-colors hover:text-gold-300 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" /> {t("admin_ingest_add_row")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Published events */}
        <div className="rounded-xl border border-white/10 bg-ink-800/20 overflow-hidden shadow-sm">
          <div className="flex items-center gap-2 border-b border-white/10 px-6 py-4">
            <FileText className="h-4 w-4 text-gold-400" />
            <h2 className="font-display text-lg font-bold text-paper-0">{t("admin_ingest_pub_title")}</h2>
          </div>
          {loadingPublished ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-gold-400" /></div>
          ) : published.length > 0 ? (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                <tr>
                  <th className="px-6 py-3">{t("admin_ingest_col_event")}</th>
                  <th className="px-6 py-3">{t("admin_ingest_col_race")}</th>
                  <th className="px-6 py-3">{t("admin_ingest_col_gender")}</th>
                  <th className="px-6 py-3">{t("admin_ingest_col_results")}</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {published.map((ev) => (
                  <tr key={ev.event_name} className="hover:bg-white/[0.02]">
                    <td className="px-6 py-3 text-mist-300">{siteEventsById[ev.event_id]?.title ?? "—"}</td>
                    <td className="px-6 py-3 text-paper-0">{ev.event_name}</td>
                    <td className="px-6 py-3 text-mist-300">{ev.gender}</td>
                    <td className="px-6 py-3 text-mist-300">{ev.count}</td>
                    <td className="px-6 py-3">
                      <button
                        onClick={() => setDeleteTarget(ev.event_name)}
                        disabled={deletingEvent === ev.event_name}
                        aria-label={`Delete ${ev.event_name}`}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-400 transition-colors hover:bg-red-500 hover:text-white disabled:opacity-50"
                      >
                        {deletingEvent === ev.event_name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="py-12 text-center text-sm text-mist-500">{t("admin_ingest_no_results")}</p>
          )}
        </div>
      </main>

      <ConfirmModal
        open={!!deleteTarget}
        title={t("admin_ingest_delete_title")}
        message={deleteTarget ? `${t("admin_ingest_delete_msg1")}${deleteTarget}${t("admin_ingest_delete_msg2")}` : ""}
        confirmLabel={t("admin_ingest_delete_btn")}
        busy={!!deletingEvent}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeletePublishedEvent}
      />
    </div>
  )
}
